import { Observable, Subject } from 'rxjs';
import { AttachmentType, FieldType, RoleLevel } from '@app/origami-api/constants';
import { UserProvider } from '@app/origami-api/constants/user-provider';
import { SimpleTopic, TimeZoneInfo } from '@app/origami-api/utils/topic-utils';
import { OrigamiHttpResponse } from '@app/origami-api/services/base/service.base';
import { SpecialValidationType } from '@app/origami-api/constants/validation-type';
import { ISearchResults } from '@app/origami-api/services/index-search.service';
import { ConfigServiceModel } from '@app/origami-api/services/config.service';
import {
  LayoutField,
  LayoutGroup,
  LayoutSection,
} from '@app/pages/main/pages/quality/pages/quality-settings/pages/form-setting/components/form-layout-editor-modal/components/form-layout-editor/models';
import { InvestigativeSiteSecurityCheckLevel } from '@app/origami-api/services/investigative-site.service';
import { WritableSignal } from '@angular/core';
import { FieldOptions } from '@app/origami-api/models/field.models';

export class FieldConfig {
  static create(name, title, value, type) {
    return new FieldConfig(name, title, value, type);
  }

  constructor(
    public name: string,
    public title: string,
    public value: any,
    public type: FieldType,
  ) {}
}

export class FormConfig {
  formId: number;
  formName: string;
  formTypeId: number;
  topicTypeId: number;
  siteId: number;
  lockedByName?: string;
  formFields: FormFieldConfig[];
  collectionMap?: Record<string, (LayoutField | LayoutGroup)[]>;
  sections?: LayoutSection[];
  fieldsDependencies?: FieldsDependency[];
}

export interface CopyPasteState {
  visibility: { value: WritableSignal<boolean> };
  change: WritableSignal<boolean>;
  copiedFields: object;
}

export interface ValidationSettings {
  type: SpecialValidationType;
  options?: ValidationSettingsOptions;
}

export type ValidationSettingsOptions = RangeOptions | AllowedCharsOptions | DuplicateStringOptions;

export const SUPPORT_VALIDATION_FIELD_TYPES = new Set([FieldType.Date, FieldType.Text]);

export interface RangeOptions {
  from: string;
  to: string;
}

export interface AllowedCharsOptions {
  allowedChars: string;
}

export interface DuplicateStringOptions {
  currentName: string;
  isEventCopy?: boolean;
}

export class FormFieldConfig {
  fieldName: string;
  fieldValue?: string;
  fieldTitle: string;
  fieldType: number;
  flags: number;
  description: string;
  categoryIds: number[];
  sortOrder: number;
  facetSortOrder?: number;
  defaultTitle: string;
  hasSqlIndex: boolean;
  isUsedInCustomForms: boolean;
  facetedSearchField?: boolean;
  relatedTo: string;
  readOnlyFlags: number;
  parent?: string;
  collectionId?: number;
  width?: number;
  lookupFieldValues: {
    value: string;
    name?: string;
  }[];
  keywordSetId?: number;
  UIData?: {
    readonly?: boolean;
    maxFieldLength?: number; // maximum characters length for the field
    pattern?: RegExp;
    auditProfileId?: number;
    auditStatusTopicId?: number;
    isSingleSelect?: boolean;
    isLinkedRoomMetadataMode?: boolean;
    isWide?: boolean;
    isNewEntity?: boolean;
    isDraftEntity?: boolean;
    hideLabel?: boolean;
    hideError?: boolean;
    copyPasteState?: CopyPasteState;
    siteId?: number;
    recordRoomId?: number;
    topicTypeId?: number;
    enumValues?: any;
    enumStrings?: any;
    allowedExt?: string | string[];
    allowedAttachmentType?: AttachmentType[];
    userProvider?: UserProvider;
    usersFilter?: (data: any) => any;
    displayNameTemplate?: (data: any) => any;
    maxRoleLevel?: RoleLevel;
    minRoleLevel?: RoleLevel;
    contactType?: number;
    valueChange?: (value: any) => any;
    apiInteraction?: {
      request?: Observable<any>;
      requestWithSearch?: (text?: string) => Observable<any>;
      convertItem: (item) => any;
      convertResponse?: (response: OrigamiHttpResponse) => any[];
    };
    filter?: (item: SimpleTopic) => boolean;
    typeFieldPreselectedValueGetter?: (topics: SimpleTopic[]) => any;
    idFieldName?: string;
    defaultItem?: any;
    validation?: ValidationSettings;
    editMode?: boolean;
    editModeGetter?: () => boolean;
    siteUserInvSitesGetter?: () => number[];
    defaultValueFromCustomForm?: any;
    hasActualDbValue?: boolean;
    additionalRequestParams?: any;
    formReadySubject?: Subject<any>;
    placeholder?: string;
    entity?: any;
    additionalIcon?: string;
    descriptionIcon?: string;
    colorIcon?: string;
    valueField?: FormValueField;
    facetedSearchResult?: ISearchResults;
    searchMode?: boolean;
    isAdvancedSearch?: boolean;
    needsImmediateAdvancedSearchResult?: boolean;
    maxDate?: Date;
    minDate?: Date;
    minDateFromField?: string;
    maxDateFromField?: string;
    minDateFromFieldTitle?: string;
    maxDateFromFieldTitle?: string;
    displaySettings?: any;
    autocompleteProperties?: {
      allowCustom?: boolean;
      numberValidation?: boolean;
    };
    useInSSU?: boolean;
    useInSSUDocuments?: boolean;
    appliedValue?: any;
    roomTimeZone?: TimeZoneInfo;
    topicFieldNameUsedInSearch?: string;
    baseSearchQuery?: string;
    searchByName?: boolean;
    displayIdInsteadOfName?: boolean;
    hasClearButton?: boolean;
    hasSelectButton?: boolean;
    selectButtonPosition?: 'top' | 'bottom';
    selectButtonLabel?: string;
    selectButtonIconClass?: string;
    onSelectButtonClick?: () => Observable<unknown> | void;
    useNameAsValue?: boolean;
    hideTimePart?: boolean;
    applyEndOfDayTime?: boolean;
    securityCheckLevel?: InvestigativeSiteSecurityCheckLevel;
    appendTo?: 'root' | 'component';
    contact?: {
      investigativeSiteId?: number;
      investigativeSiteField?: string;
    };
  };
  options?: FieldOptions;
  renderer?: any;
  customFieldSettings?: any; // TODO: somehow combine this with UIData
  showInlineDescription?: boolean;
}

export type BeStoredUIData = Pick<FormFieldConfig['UIData'], 'validation'>;

export interface FieldsDependency {
  Title: string;
  Actions: FieldsDependencyAction[];
  Condition: FieldsDependencyCondition;
}

export interface FieldsDependencyAction {
  FieldName: string;
  SetFlags?: number;
  ResetFlags?: number;
  SetValue?: any;
  SetValueFrom?: string;
}

export interface FieldsDependencyCondition {
  FieldName: string;
  FieldValue: string;
  ConditionId?: number;
}

export interface DynamicFormData {
  defaultForm?: ConfigServiceModel.Form;
  fields: FieldConfig[];
  form?: FormConfig;
  id?: number;
  queryTickets?: any;
  lockedBy?: number | null;
  workflows?: any;
  needCustomForm?: boolean;
  isCreation?: boolean;
  defaultFields?: Partial<ConfigServiceModel.Field>[];
}

export enum FormValueField {
  FullName = 'FullName',
  UserId = 'UserId',
}
